import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';

@Pipe({
    name: 'filterTags',
})
@Injectable()
export class FilterTagsPipe implements PipeTransform {
    // NOTE: keep this pipe pure, but allow callers to pass a "layout key" (e.g. loading flag)
    // so the pipe re-runs when the element becomes measurable (offsetWidth changes).
    transform(value: MaxBrainTag[], content: HTMLElement, component: { visualLength: (text: string) => number }, _layoutKey?: unknown): MaxBrainTag[] {
        if (!value) {
            return [];
        }
        if (!content) {
            return value;
        }

        // Prefer the host width, but fall back to the parent when the host shrink-wraps to its
        // content (common with flex align-items: flex-start). Otherwise we under-measure and
        // incorrectly show the "..." overflow pill even when tags would fit.
        const hostWidth = content.offsetWidth || 0;
        const parentWidth = content.parentElement?.clientWidth || 0;
        const contentWidth = Math.max(hostWidth, parentWidth);
        if (contentWidth <= 0) {
            return [];
        }

        // Heuristic sizing: chip has horizontal padding + borders; "..." has its own pill width.
        const chipExtraWidth = 32; // padding + borders + internal spacing
        const chipGap = 6; // gap between chips / "..."
        const morePillWidth = 26; // matches .more-tags width

        const tagWidth = (tag: MaxBrainTag): number => component.visualLength(tag.name) + chipExtraWidth + chipGap;

        // Pass 1: try to fit every tag in one row (no "..." reserve).
        let remainingWidth = contentWidth - 4;
        const allFit = value.every((tag) => {
            const width = tagWidth(tag);
            if (remainingWidth >= width) {
                remainingWidth -= width;
                return true;
            }
            return false;
        });
        if (allFit) {
            return value;
        }

        // Pass 2: reserve space for the "..." pill, then fit as many leading tags as possible.
        remainingWidth = contentWidth - 4 - morePillWidth - chipGap;
        const filteredTags: MaxBrainTag[] = [];
        for (const tag of value) {
            const width = tagWidth(tag);
            if (remainingWidth >= width) {
                remainingWidth -= width;
                filteredTags.push(tag);
            } else {
                break;
            }
        }

        return filteredTags;
    }
}
